import type { FC, ReactNode } from 'react'
import type { JournalNote } from '@/types'

import Image from 'next/image'
import { createPortal } from 'react-dom'
import { ArrowRight, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatShortDate } from '@/lib/dates'
import { journalNotes } from '@/constants/content'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Reveal, RevealText } from '@/components/generals/reveal'
import { useScrollLock } from '@/components/generals/smooth-scroll'

const SOFT_EASE = [0.22, 1, 0.36, 1] as const
const CURTAIN_EASE = [0.76, 0, 0.24, 1] as const

interface NoteCardProps {
    note: JournalNote
    wide?: boolean
    onOpen: () => void
}

/**
 * Article card in the same dark, padded style as the work cards. The wide
 * card sets its tall image beside the text on larger screens.
 */
const NoteCard: FC<NoteCardProps> = ({ note, wide = false, onOpen }): ReactNode => {
    return (
        <button
            type='button'
            onClick={onOpen}
            data-cursor='Read'
            aria-label={`Read "${note.title}", ${note.tag}, ${formatShortDate(note.date)}`}
            className={cn(
                'group block h-full w-full rounded-[10px] bg-[#131313] p-2.5 text-left transition-colors duration-500 hover:bg-[#171717]',
                wide && 'md:grid md:grid-cols-12 md:gap-6'
            )}
        >
            <span className={cn('relative block aspect-[4/5] overflow-hidden rounded-[7px] bg-surface', wide ? 'md:col-span-5' : 'md:aspect-[5/6]')}>
                <Image
                    src={note.image.src}
                    alt={note.image.alt}
                    fill
                    sizes={wide ? '(min-width: 768px) 40vw, 92vw' : '(min-width: 768px) 48vw, 92vw'}
                    className='object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]'
                />
            </span>

            <span className={cn('flex flex-col px-2.5 pb-3 pt-5 md:px-3 md:pb-4', wide && 'md:col-span-7 md:justify-end md:px-4 md:pb-6')}>
                <span className='flex items-center justify-between gap-4 text-xs text-muted-foreground'>
                    <span>
                        <span aria-hidden='true' className='mr-1.5 text-gold'>/</span>
                        {note.tag}
                    </span>

                    <time dateTime={note.date} className='tabular-nums'>
                        {formatShortDate(note.date)}
                    </time>
                </span>

                <span
                    className={cn(
                        'mt-3 block font-light tracking-[-0.03em] text-foreground',
                        wide ? 'text-[clamp(1.6rem,3.4vw,3.25rem)] leading-[1.02]' : 'text-[clamp(1.35rem,2vw,1.85rem)] leading-[1.1]'
                    )}
                >
                    {note.title}
                </span>

                {wide && (
                    <span className='mt-5 hidden max-w-md leading-relaxed text-muted-foreground md:block'>
                        {note.intro}
                    </span>
                )}

                <span className='mt-6 inline-flex items-center gap-2 text-sm text-foreground'>
                    <span className='underline decoration-white/25 underline-offset-[6px] transition-colors duration-300 group-hover:text-gold group-hover:decoration-gold'>
                        Read the note
                    </span>

                    <ArrowRight className='size-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:text-gold' aria-hidden='true' />
                </span>
            </span>
        </button>
    )
}

interface NoteReaderProps {
    index: number | null
    onClose: () => void
    onNavigate: (index: number) => void
}

/**
 * A clean reading view that rises over the page, since the notes have no
 * pages of their own. Escape closes it and focus stays inside while it is open.
 */
const NoteReader: FC<NoteReaderProps> = ({ index, onClose, onNavigate }): ReactNode => {
    const [mounted, setMounted] = useState(false)
    const open = index !== null

    useEffect(() => setMounted(true), [])
    useScrollLock(open)

    if (!mounted) return null

    return createPortal(
        <AnimatePresence>
            {open && index !== null && (
                <ReaderPanel key='reader' index={index} onClose={onClose} onNavigate={onNavigate} />
            )}
        </AnimatePresence>,
        document.body
    )
}

interface ReaderPanelProps {
    index: number
    onClose: () => void
    onNavigate: (index: number) => void
}

const ReaderPanel: FC<ReaderPanelProps> = ({ index, onClose, onNavigate }): ReactNode => {
    const panelRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const closeRef = useRef<HTMLButtonElement>(null)
    const returnFocusRef = useRef<HTMLElement | null>(null)
    const note = journalNotes[index]
    const next = journalNotes[(index + 1) % journalNotes.length]

    useEffect(() => {
        returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        closeRef.current?.focus()

        return () => returnFocusRef.current?.focus()
    }, [])

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: 0 })
    }, [index])

    useEffect(() => {
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                onClose()
                return
            }

            if (event.key !== 'Tab') return

            const focusable = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [])
            const first = focusable[0]
            const last = focusable[focusable.length - 1]

            if (!first || !last) return

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', handleKey)

        return () => document.removeEventListener('keydown', handleKey)
    }, [onClose])

    return (
        <motion.div
            ref={panelRef}
            role='dialog'
            aria-modal='true'
            aria-labelledby='note-title'
            className='fixed inset-0 z-[70] flex flex-col bg-background'
            initial={{ clipPath: 'inset(100% 0% 0% 0%)' }}
            animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
            exit={{ clipPath: 'inset(100% 0% 0% 0%)' }}
            transition={{ duration: 0.8, ease: CURTAIN_EASE }}
        >
            <div className='shell flex h-18 shrink-0 items-center justify-between border-b border-white/10'>
                <p className='text-sm text-muted-foreground'>
                    <span aria-hidden='true' className='mr-1.5 text-gold'>/</span>
                    Journal
                </p>

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    className='flex size-11 items-center justify-center rounded-full border border-white/15 transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-ink'
                >
                    <X className='size-5' aria-hidden='true' />

                    <span className='sr-only'>
                        Close the note
                    </span>
                </button>
            </div>

            <div ref={scrollRef} data-lenis-prevent className='min-h-0 flex-1 overflow-y-auto overscroll-contain'>
                <AnimatePresence mode='wait' initial={false}>
                    <motion.article
                        key={note.slug}
                        className='shell pb-24 pt-14 md:pb-32 md:pt-20'
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.2, ease: SOFT_EASE } }}
                        exit={{ opacity: 0, y: -16, transition: { duration: 0.3, ease: SOFT_EASE } }}
                    >
                        <div className='mx-auto max-w-3xl'>
                            <p className='flex flex-wrap items-center gap-3 text-sm text-muted-foreground'>
                                <span className='rounded-full border border-gold/50 px-3 py-1 text-xs text-gold'>
                                    {note.tag}
                                </span>

                                <time dateTime={note.date}>{formatShortDate(note.date)}</time>
                                <span aria-hidden='true' className='h-px w-5 bg-white/20' />
                                <span>{note.readingTime}</span>
                            </p>

                            <h2 id='note-title' className='type-display mt-8 text-foreground'>
                                {note.title}
                            </h2>

                            <p className='mt-8 text-xl font-light leading-relaxed tracking-[-0.01em] text-foreground/90 md:text-2xl'>
                                {note.intro}
                            </p>
                        </div>

                        <div className='relative mx-auto mt-14 aspect-[16/10] max-w-5xl overflow-hidden rounded-[4px] bg-surface md:mt-20'>
                            <Image
                                src={note.image.src}
                                alt={note.image.alt}
                                fill
                                sizes='(min-width: 1024px) 64rem, 92vw'
                                className='object-cover'
                            />
                        </div>

                        <div className='mx-auto mt-14 max-w-[62ch] space-y-6 text-lg leading-[1.75] text-foreground/80 md:mt-20'>
                            {note.body.map(paragraph => (
                                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
                            ))}

                            <p className='flex items-center gap-3 pt-6 text-sm text-muted-foreground'>
                                <span aria-hidden='true' className='h-px w-8 bg-gold' />
                                Aurelia Voss, Auren
                            </p>
                        </div>

                        <div className='mx-auto mt-20 max-w-3xl border-t border-white/10 pt-10'>
                            <button
                                type='button'
                                onClick={() => onNavigate((index + 1) % journalNotes.length)}
                                className='group flex w-full items-center justify-between gap-6 text-left'
                            >
                                <span>
                                    <span className='block text-sm text-muted-foreground'>Next note</span>

                                    <span className='mt-2 block text-[clamp(1.6rem,3vw,2.5rem)] font-light leading-tight tracking-[-0.035em] text-foreground transition-colors duration-300 group-hover:text-gold'>
                                        {next.title}
                                    </span>
                                </span>

                                <span className='flex size-12 shrink-0 items-center justify-center rounded-full border border-white/15 transition-colors duration-500 group-hover:border-gold group-hover:bg-gold group-hover:text-ink'>
                                    <ArrowRight className='size-4 transition-transform duration-500 group-hover:translate-x-0.5' aria-hidden='true' />
                                </span>
                            </button>
                        </div>
                    </motion.article>
                </AnimatePresence>
            </div>
        </motion.div>
    )
}

export const Journal: FC = (): ReactNode => {
    const [active, setActive] = useState<number | null>(null)
    const [featured, ...rest] = journalNotes

    const close = useCallback(() => setActive(null), [])

    return (
        <section id='journal' aria-labelledby='journal-title' className='px-4 py-28 md:px-[30px] md:py-40'>
            <div className='flex flex-wrap items-end gap-x-10 gap-y-6'>
                <div>
                    <Eyebrow>Journal</Eyebrow>

                    <RevealText
                        id='journal-title'
                        as='h2'
                        text='Notes on design and craft'
                        className='type-display mt-6 max-w-[12ch]'
                    />
                </div>

                <Reveal delay={0.15} className='pb-2'>
                    <button
                        type='button'
                        onClick={() => setActive(0)}
                        className='group inline-flex items-center gap-3 text-sm font-medium text-foreground'
                    >
                        <span className='relative'>
                            More notes
                            <span aria-hidden='true' className='absolute -bottom-1 left-0 h-px w-full bg-white/20' />
                            <span aria-hidden='true' className='absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100' />
                        </span>

                        <ArrowRight className='size-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:text-gold' aria-hidden='true' />
                    </button>
                </Reveal>
            </div>

            <div className='mt-14 grid gap-5 md:mt-20 md:grid-cols-2'>
                <Reveal y={36} className='md:col-span-2'>
                    <NoteCard note={featured} wide onOpen={() => setActive(0)} />
                </Reveal>

                {rest.map((note, index) => (
                    <Reveal key={note.slug} y={36} delay={index * 0.1}>
                        <NoteCard note={note} onOpen={() => setActive(index + 1)} />
                    </Reveal>
                ))}
            </div>

            <NoteReader index={active} onClose={close} onNavigate={setActive} />
        </section>
    )
}
